/**
 * Event name for {@link DismissOverlaysEvent}. Exported so consumers can listen
 * for or dispatch the event without hardcoding the string.
 */
export const DISMISS_OVERLAYS_EVENT = 'nord-dismiss-overlays'

/**
 * Dispatched on `document` immediately **before** an overlay opens, asking any
 * open overlays (popouts, dropdowns, comboboxes, tooltips) to dismiss themselves
 * so the newly opening overlay is the topmost surface.
 *
 * Nord overlays enter the browser top layer (via the `popover` API), so no
 * `z-index` can place a `nord-modal` above an already-open `nord-popout`, and
 * nothing else dismisses it. `nord-modal` dispatches this event before it
 * becomes visible; subscribed overlays hide in response.
 *
 * Applications with their own (non-Nord) overlays SHOULD dispatch it the same
 * way, **before** their overlay becomes visible:
 *
 * ```ts
 * import { DismissOverlaysEvent } from "@nordhealth/components"
 *
 * function openMyOverlay() {
 *   // 1. Ask any open Nord overlays to dismiss — BEFORE yours becomes visible.
 *   document.dispatchEvent(new DismissOverlaysEvent())
 *   // 2. Then open your own overlay.
 *   myOverlay.showModal()
 * }
 *
 * // Runs client-side (a user interaction), so `document` is available.
 * openButton.addEventListener("click", openMyOverlay)
 * ```
 *
 * Without the import (plain HTML/JS) the equivalent is
 * `document.dispatchEvent(new Event("nord-dismiss-overlays"))`.
 *
 * Ordering matters: dispatching **after** the overlay is visible would dismiss
 * overlays that live *inside* it. Dispatching before it opens leaves them
 * untouched, because they are not open yet.
 *
 * The event is dispatched on `document` and does not cross iframe boundaries.
 * Dispatch it from a client (browser) context — e.g. an interaction handler or
 * after mount — not during server-side rendering, since there is no `document`
 * on the server (as with any DOM API in an SSR app such as Nuxt).
 */
export class DismissOverlaysEvent extends Event {
  constructor(eventInitDict?: EventInit) {
    super(DISMISS_OVERLAYS_EVENT, eventInitDict)
  }
}
